import { Component, ElementRef, OnInit } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggerService, UserSessionService } from 'bna-angular-auth-client';
import { Subscription } from 'rxjs';
import { ClientSettingsService } from 'src/app/core/services/auth/client-settings.service';
import { OrganizationService } from 'src/app/core/services/http/agency-mgmt/organization.service';
import { SettingsService } from 'src/app/core/services/http/settings.service';
import { OnboardingStepOneComponentComponent } from 'src/app/settings/components/onboarding/onboarding-step-one-component/onboarding-step-one-component.component';
import { OnboardingStepThreeComponentComponent } from 'src/app/settings/components/onboarding/onboarding-step-three-component/onboarding-step-three-component.component';
import { OnboardingStepTwoComponentComponent } from 'src/app/settings/components/onboarding/onboarding-step-two-component/onboarding-step-two-component.component';
import { StepSharedService } from 'src/app/settings/components/onboarding/stepshared.service';
import { OnboardingStatusResult } from 'src/app/settings/models/onboarding-status';
import { UserGroupService } from 'src/app/settings/services/user.group.service';
// import { IntegrationTypes } from 'src/app/settings/models/integration-type.model';
import { AppSettings } from 'src/app/shared/constants/app-settings.constants';
import { OrganizationStartupSettingsResult } from 'src/app/shared/models/agency-mgmt/manager.service.model';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
})
export class SignInComponent implements OnInit {
  private signing = false;
  private returnUrl = '';
  private subscriptions: Subscription[] = [];
  public errorMessage: string;
  opendialogRef: MatDialogRef<any>; // Keep a reference to the currently open modal
  onboardingStatusResult: OnboardingStatusResult;
  disableOnboardingFlow: boolean = false;
  isEnablePaymentBanner: boolean = false;
  isLeadOrgAdmin: boolean;
  isKeyManagerFirstLoggin: boolean = false;

  constructor(
    private logger: LoggerService,
    public userSessionService: UserSessionService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private clientSettingsService: ClientSettingsService,
    private organizationService: OrganizationService,
    private settingsService: SettingsService,
    private sharedService: StepSharedService,
    private dialogRef: MatDialog,
    private userGroupService: UserGroupService
  ) {}

  async ngOnInit() {
    let integrationType =
      this.activatedRoute.snapshot?.queryParams['integration-type'];
    localStorage.setItem('integration-type', integrationType);

    this.signing = await this.userSessionService.signing();
    this.returnUrl = this.getReturnUrl();
    await this.redirectToSignInPageIfNotSignedIn();
    this.redirectToDefaultPageAfterSignedIn();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private getReturnUrl() {
    return this.activatedRoute.snapshot?.queryParams['returnUrl']
      ? this.activatedRoute.snapshot?.queryParams['returnUrl'][0]
      : null;
  }

  // Added onboarding check before redirect
  private redirectToDefaultPageAfterSignedIn() {
    this.userSessionService.whenSignedInCompleted().then(() => {
      this.signing = false;
      this.logger.log('redirectToDefaultPageAfterSignedIn');
      this.appEnableValidityCheckPassed().then((passed) => {
        if (passed) {
          this.loadOnboardingStatus();
          return;
        }

        this.userSessionService.signOut(true);
      });
    });
  }

  private appEnableValidityCheckPassed() {
    return new Promise<boolean>((resolve) => {
      this.isAppEnabledForCurrentOrganization().then((passed) => {
        if (passed) {
          return resolve(passed);
        }
        return resolve(false);
      });
    });
  }

  private async redirectToSignInPageIfNotSignedIn() {
    const yes = this.userSessionService.isIntegration;
    this.logger.log('iframe integration check: ' + yes);

    if (yes || this.signing) return;

    this.logger.log('redirectToSignInPageIfNotSignedIn');
    await this.signIn();
  }

  private async signIn() {
    const isAuthenticated = this.userSessionService.isAuthenticated();
    if (isAuthenticated) {
      return; // Let whenSignedInCompleted callback handle redirect
    }
    this.signing = true;
    await this.userSessionService.signIn();
    // Let whenSignedInCompleted callback handle redirect
  }

  private async redirectToDefaultPage() {
    if (this.returnUrl) {
      this.logger.log('redirecting to return url: ' + this.returnUrl);
      this.router.navigate([this.returnUrl]);
      return;
    } else {
      this.logger.log('redirecting to default page');
      await this.router.navigate(['/calendar']);
      return;
    }
  }

  private isAppEnabledForCurrentOrganization() {
    return new Promise<boolean>((resolve) => {
      const passed = this.configurationCheckPassed;
      if (passed) return resolve(true);
      let integrationType = localStorage.getItem('integration-type');
      if (integrationType === 'IM') {
        this.organizationService.getStartupSettings().subscribe((result) => {
          if (result != null && !result.isKeyAutomateEnabled) {
            this.router.navigate(['static-pages/ka-intro']);
          }
          this.rememberAppEnabledOrganization(result);
          return resolve(result.isKeyAutomateEnabled);
        });
      } else {
        return resolve(true);
      }
    });
  }

  private rememberAppEnabledOrganization(
    result: OrganizationStartupSettingsResult
  ) {
    if (!result?.keyManagerEnabled) return;
    this.clientSettingsService.set(
      AppSettings.AppEnabledOrganizationId,
      this.userSessionService.getProfile().organizationId,
      false
    );
  }

  public get configurationCheckPassed() {
    const enabledOrganizationId = this.clientSettingsService.get(
      AppSettings.AppEnabledOrganizationId,
      false
    );
    return (
      !!enabledOrganizationId &&
      enabledOrganizationId ==
        this.userSessionService.getProfile().organizationId
    );
  }

  // Added redirect after onboarding complete
  loadOnboardingStatus() {
    this.isLeadOrgAdmin = this.userGroupService.isUserLeadOrgAdmin();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = '1250px';
    dialogConfig.height = '600px';
    dialogConfig.disableClose = true;
    dialogConfig.panelClass = 'onboarding-dialog';

    this.settingsService
      .getOnboardingStatus()
      .toPromise()
      .then((res) => {
        this.onboardingStatusResult = res;
        localStorage.setItem(
          'isPropertyTreeFreePlan',
          this.onboardingStatusResult.isPropertyTreeFreePlan.toString()
        );
        this.disableOnboardingFlow =
          this.onboardingStatusResult.onboardingStatusId == 1 &&
          this.onboardingStatusResult.isKeyAutomateFirstLogging == false
            ? true
            : false;

        this.isKeyManagerFirstLoggin =
          this.onboardingStatusResult.onboardingStatusId == 0 &&
          this.onboardingStatusResult.isKeyAutomateFirstLogging == false &&
          this.isLeadOrgAdmin == false
            ? true
            : false;

        if (
          this.onboardingStatusResult.onboardingStatusId == 0 ||
          this.disableOnboardingFlow
        ) {
          this.openModal(1, dialogConfig);
        } else {
          // No onboarding needed, redirect directly
          this.redirectToDefaultPage();
        }

        this.isEnablePaymentBanner =
          this.onboardingStatusResult.onboardingStatusId == 1 &&
          this.onboardingStatusResult.isKeyAutomateFirstLogging == true &&
          this.onboardingStatusResult.isPaid == false;

        this.sharedService.getStep().subscribe((step: number) => {
          if (step != 0) {
            this.opendialogRef.close();
            if (step != 4) {
              this.openModal(step, dialogConfig);
            } else {
              // ✅ ADDED: Step 4 = onboarding complete, now redirect
              this.redirectToDefaultPage();
            }
          }
        });
      });
  }

  openModal(step: number, dialogConfig: MatDialogConfig) {
    dialogConfig.data = {
      disableOnboardingFlow: this.disableOnboardingFlow,
      isKeyManagerFirstLoggin: this.isKeyManagerFirstLoggin,
      agencyName: this.onboardingStatusResult.agencyName,
      promotionalVideoLink: this.onboardingStatusResult.promotionalVideoLink,
      isLeadOrgAdmin: this.isLeadOrgAdmin,
      isLeadOrgAdminComplete:
        this.onboardingStatusResult.isLeadOrgAdminComplete,
    };
    switch (step) {
      case 1:
        if (
          this.dialogRef.openDialogs.some(
            (dialogRef) =>
              dialogRef.componentInstance instanceof
              OnboardingStepOneComponentComponent
          )
        ) {
          return;
        }
        this.opendialogRef = this.dialogRef.open(
          OnboardingStepOneComponentComponent,
          dialogConfig
        );
        break;
      case 2:
        if (
          this.dialogRef.openDialogs.some(
            (dialogRef) =>
              dialogRef.componentInstance instanceof
              OnboardingStepTwoComponentComponent
          )
        ) {
          return;
        }
        this.opendialogRef = this.dialogRef.open(
          OnboardingStepTwoComponentComponent,
          dialogConfig
        );
        break;
      case 3:
        if (
          this.dialogRef.openDialogs.some(
            (dialogRef) =>
              dialogRef.componentInstance instanceof
              OnboardingStepThreeComponentComponent
          )
        ) {
          return;
        }
        this.opendialogRef = this.dialogRef.open(
          OnboardingStepThreeComponentComponent,
          dialogConfig
        );
        break;
    }
  }
}
